import produce from 'immer';
import { useState } from 'react';
import { RiSaveLine } from 'react-icons/ri';
import { useRecoilState } from 'recoil';
import LoadingIndicator from '../../components/LoadingIndicator';

import Modal from '../../components/modals/Modal';
import { INTERVAL_SELECTIONS } from './const';
import { useApiId, ApiEditFormState, useUpdateApi } from './ViewApiHooks';

import useAuth from '../../hooks/auth';
import { COMMUNITY } from '../auth/AuthState';
import { BsFillInfoCircleFill } from 'react-icons/bs';
import envs from '../../config/envs';
import { analytics } from '../../App';
import { toast } from 'react-toastify';

export default function ApiSettingModal({
  openSetting,
  setOpenSetting,
}: {
  openSetting?: boolean;
  setOpenSetting: (v: boolean) => void;
}) {
  const apiId = useApiId();
  const [values] = useRecoilState(ApiEditFormState(apiId));
  const [{ rpcStatus }, { updateApi }] = useUpdateApi();

  const [formValues, setFormValues] = useState(values);

  const [{ auth }] = useAuth();

  return (
    <Modal open={openSetting}>
      <div className='text-center text-xl font-bold'>Setting</div>
      <div className='flex flex-col gap-3'>
        <div className='form-control'>
          <label className='label'>
            <span className='label-text'>Title</span>
          </label>
          <input
            type='text'
            className='input bg-base-300'
            placeholder='Please enter title'
            value={formValues.displayName}
            maxLength={125}
            onChange={(e) => {
              setFormValues(
                produce(formValues, (draft) => {
                  draft.displayName = e.target.value;
                })
              );
            }}
          />
        </div>
        <div className='form-control'>
          <label className='label'>
            <span className='label-text'>Description</span>
          </label>
          <textarea
            className='textarea bg-base-300'
            placeholder='Please enter description'
            value={formValues.description}
            onChange={(e) => {
              setFormValues(
                produce(formValues, (draft) => {
                  draft.description = e.target.value;
                })
              );
            }}
          />
        </div>
        {
          !formValues.database?.startsWith('realtime') && <div className='form-control'>
            <label className='label'>
              <span className='label-text'>DATA REFRESH INTERVAL</span>
            </label>
            <div className='w-full'>
              <select
                value={formValues.dataIngestIntervalSeconds || 0}
                className='select select-xs w-full no-outline py-0 min-h-[5rem] bg-transparent font-normal active:outline-none focus:outline-none'
                onChange={async (e) => {
                  setFormValues(
                    produce(formValues, (draft) => {
                      draft.dataIngestIntervalSeconds = parseInt(
                        e.target.value,
                        10
                      );
                    })
                  );
                }}
              >
                {INTERVAL_SELECTIONS.map((n) => (
                  <option key={n.value} value={n.value}>
                    {n.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        }
        {!envs.isCircleIntegrationEnabled && <div className='form-control'>
          <label className='label'>
            <span className='label-text flex'>Privacy setting</span>
          </label>
          <div className='form-control'>
            <label className='label cursor-pointer'>
              <span className='label-text text-xs flex items-center'>
                Private (Only visible to me)
                {auth?.tenant === COMMUNITY && (
                  <span
                    className='tooltip ml-1 tooltip-warning tooltip-top flex items-center justify-center opacity-90 cursor-pointer'
                    data-tip='Please upgrade the plan to make userapi private'
                  >
                    <BsFillInfoCircleFill
                      size='0.7rem'
                      className='mx-1 text-blue-600'
                    />
                  </span>
                )}
              </span>
              <input
                checked={formValues.access === 'private'}
                disabled={auth?.tenant === COMMUNITY} // NOTE: will also need to check the plan once available
                onChange={() => {
                  setFormValues(
                    produce(formValues, (draft) => {
                      // @ts-ignore
                      draft.access = 'private';
                    })
                  );
                }}
                type='radio'
                name='access_level'
                className='radio checked:bg-blue-500'
              />
            </label>
          </div>
          {auth?.tenant !== COMMUNITY && (
            <div className='form-control'>
              <label className='label cursor-pointer'>
                <span className='label-text text-xs'>
                  Org Internal (Visible to my org)
                </span>

                <input
                  checked={formValues.access === 'org_internal'}
                  onChange={() => {
                    setFormValues(
                      produce(formValues, (draft) => {
                        // @ts-ignore
                        draft.access = 'org_internal';
                      })
                    );
                  }}
                  type='radio'
                  name='access_level'
                  className='radio checked:bg-blue-500'
                />
              </label>
            </div>
          )}
          <div className='form-control'>
            <label className='label cursor-pointer'>
              <span className='label-text text-xs'>
                Public (Visible to everyone)
              </span>
              <input
                checked={formValues.access === 'public'}
                onChange={() => {
                  setFormValues(
                    produce(formValues, (draft) => {
                      // @ts-ignore
                      draft.access = 'public';
                    })
                  );
                }}
                type='radio'
                name='access_level'
                className='radio checked:bg-blue-500'
              />
            </label>
          </div>
        </div>}

        <div className='flex gap-2 items-center'>
          <button
            className='btn btn-primary gap-4'
            onClick={async () => {
              const success = await updateApi(formValues);

              analytics.track('API - Update API settings', {
                updated: true,
                values: formValues,
              })

              if (success) {
                toast.success('Api updated successfully', {
                  autoClose: 2000,
                  position: 'top-center',
                })
                setOpenSetting(false);
              }
            }}
          >
            <RiSaveLine />
            Save
          </button>
          <button
            className='btn btn-ghost'
            style={{ border: '1px solid #aaaaaa' }}
            onClick={() => {
              setOpenSetting(false);
            }}
          >
            Cancel
          </button>
          {rpcStatus.isLoading && <LoadingIndicator />}
        </div>
      </div>
    </Modal>
  );
}
